import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useWeeklyReset } from "@/lib/useRhythm";
import { Section } from "@/components/intel/Section";
import { ItemCardEmpty } from "@/components/intel/ItemCard";

/**
 * Weekly Intelligence Reset — perspective, synthesis, reset. Editorial
 * page composed from the last 7 days of platform activity for this user.
 * Not a performance dashboard: there are no counts of "how often you
 * showed up" or streaks. Just narrative + opportunities + circles + themes.
 *
 * Reachable from a quiet "Weekly reset →" link on the Dashboard. No nav
 * entry — this is a destination you arrive at on purpose.
 */
export function WeeklyReset() {
  const { data, isLoading, isError } = useWeeklyReset();

  return (
    <div className="px-10 py-10 max-w-4xl">
      <Link
        to="/app"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-whisper hover:text-gold-glow"
      >
        <ArrowLeft className="size-3" strokeWidth={1.5} />
        Back to Intelligence
      </Link>

      <header className="mt-6 mb-12">
        <p className="label-eyebrow">Weekly reset</p>
        <h1 className="mt-2 text-display-2 font-display">The last seven days</h1>
        <p className="mt-3 text-whisper max-w-xl leading-relaxed">
          Perspective, not performance. A calm look back at what moved, what
          you've kept, and what you keep returning to.
        </p>
      </header>

      {isLoading ? (
        <ItemCardEmpty label="Reading the week…" />
      ) : isError || !data ? (
        <ItemCardEmpty label="Couldn't compose this week." />
      ) : (
        <div className="space-y-12">
          {data.narratives.length > 0 && (
            <Section eyebrow="Narratives" title="What moved this week">
              <ul className="panel divide-y divide-veil/40">
                {data.narratives.map((n) => (
                  <li key={n.id} className="p-5">
                    <a
                      href={n.url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-display text-base leading-snug hover:text-gold-glow transition-colors block"
                    >
                      {n.title}
                    </a>
                    <p className="mt-2 text-sm text-whisper leading-relaxed">{n.summary}</p>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-muted">
                      via {n.source_name}
                    </p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {data.opportunities_to_revisit.length > 0 && (
            <Section
              eyebrow="Open"
              title="Opportunities worth revisiting"
              detail="Saved but not yet acted on. Re-evaluate, archive, or pursue."
            >
              <ul className="panel divide-y divide-veil/40">
                {data.opportunities_to_revisit.map((o) => (
                  <li key={o.id} className="p-5 flex items-baseline justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-display text-base leading-snug">{o.title}</div>
                      {o.organization && (
                        <div className="mt-1 text-xs text-whisper">{o.organization}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0 text-[10px] uppercase tracking-[0.18em] text-muted">
                      saved {Math.round((Date.now() - o.saved_at) / 86400000)}d ago
                      {o.deadline_at && (
                        <div className="text-gold-glow/70">
                          due {new Date(o.deadline_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {data.circles.length > 0 && (
            <Section eyebrow="Circles" title="Rooms you're in">
              <ul className="panel divide-y divide-veil/40">
                {data.circles.map((c) => (
                  <li key={c.id} className="p-5">
                    <Link
                      to={`/app/circles/${encodeURIComponent(c.slug)}`}
                      className="font-display text-base hover:text-gold-glow transition-colors"
                    >
                      {c.name}
                    </Link>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted">
                      {c.kind} · {c.role}
                    </p>
                    {c.prompt && (
                      <p className="mt-2 text-sm text-whisper italic leading-relaxed line-clamp-2">
                        {c.prompt}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {data.themes.length > 0 && (
            <Section eyebrow="Themes" title="What you've been following">
              <div className="panel p-6">
                <div className="flex flex-wrap gap-2">
                  {data.themes.map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1.5 rounded-full text-xs border border-veil/60 text-fog"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </Section>
          )}

          {data.reflections.total > 0 && (
            <Section eyebrow="Reflections" title="Your week in your own words">
              <div className="panel p-6">
                <p className="text-sm text-fog leading-relaxed">
                  {data.reflections.total === 1
                    ? "One reflection this week."
                    : `${data.reflections.total} reflections this week.`}
                  {data.reflections.top_category && (
                    <span className="text-whisper">
                      {" "}
                      Often returning to{" "}
                      <span className="text-gold-glow/80">{data.reflections.top_category}</span>.
                    </span>
                  )}
                </p>
              </div>
            </Section>
          )}

          {data.narratives.length === 0 &&
            data.opportunities_to_revisit.length === 0 &&
            data.circles.length === 0 &&
            data.themes.length === 0 &&
            data.reflections.total === 0 && (
              <ItemCardEmpty
                label="Quiet week."
                detail="Nothing surfaced for this reset. That's fine — some weeks are like that."
              />
            )}
        </div>
      )}
    </div>
  );
}
